import { customClickReport, customClickReportSimple, expReportSimple } from '@/dataPort';

type TrackPayload = Record<string, any>;

/**
 * 新增 clstag 第三段固定为 WISP，与 TRACKING.md 的项目别名一致。
 * 最后一段由事件语义加 Click / Exposure，方便和自定义事件互相归因。
 */
export function wispClstag(eventId: string, kind: 'Click' | 'Exposure') {
  return `h|keycount|WISP|${eventId}_${kind}`;
}

/** 与 axios 业务成功判断对齐：success 或 message == 'success'，不能把 HTTP 200 当成成功。 */
export function isBusinessSuccess(res: any): boolean {
  return !!(res && (res.success || res.message == 'success'));
}

export function summarizeApiResult(res: any) {
  if (!res || typeof res !== 'object') {
    return { success: false };
  }
  const message = typeof res.msg === 'string' ? res.msg : typeof res.message === 'string' ? res.message : undefined;
  return {
    success: isBusinessSuccess(res),
    code: res.code ?? res.errCode,
    message,
  };
}

export function summarizeThrown(error: unknown) {
  if (error instanceof Error) {
    return { errorType: 'technical' as const, errorName: error.name, errorMessage: error.message };
  }
  const anyError = error as { msg?: string; message?: string; response?: { data?: { msg?: string } } } | null;
  const errorMessage = anyError?.response?.data?.msg || anyError?.msg || anyError?.message;
  return {
    errorType: 'technical' as const,
    errorMessage: typeof errorMessage === 'string' ? errorMessage : 'unknown',
  };
}

/**
 * 路由页点击。pageId 取当前路由 meta；返回值写到可点击 DOM 的 clstag，并原样带到这次点击触发的结果事件。
 * 没有 pageId 时底层会静默丢弃，所以新页面必须先补 meta.pageId。
 */
export function reportRouteClick(eventId: string, payload?: TrackPayload) {
  const clstag = wispClstag(eventId, 'Click');
  customClickReportSimple(eventId, { ...(payload || {}), clstag });
  return clstag;
}

/** 点击触发的成功/失败。clickClstag 必须是发起点击的那一个值，避免并发点击串归因。 */
export function reportRouteResult(eventId: string, payload?: TrackPayload, clickClstag?: string) {
  customClickReportSimple(eventId, {
    ...(payload || {}),
    ...(clickClstag ? { clstag: clickClstag } : {}),
  });
}

/**
 * 调用点不在页面组件里、但仍要归到指定页面时使用（例如登录请求在 util 中完成）。
 * 不依赖当前 pathname，避免工具函数被其它路由误带上 pageId。
 */
export function reportPageResult(pageId: string, eventId: string, payload?: TrackPayload, clickClstag?: string) {
  void customClickReport(pageId, eventId, {
    ...(payload || {}),
    ...(clickClstag ? { clstag: clickClstag } : {}),
  });
}

export function reportRouteExposure(eventId: string, payload?: TrackPayload) {
  const clstag = wispClstag(eventId, 'Exposure');
  expReportSimple(eventId, { ...(payload || {}), clstag });
  return clstag;
}
